import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useUserSettings } from '../../modules/admin/settings/useData';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { SearchIcon, SpinnerIcon } from './Icons';
import { SegmentedControl, type SegmentedControlOption } from './SegmentedControl';

export type GeneralListTableDensity = 'compact' | 'normal' | 'comfortable';
export const GENERAL_LIST_CELL_X = 'px-3';
export const GENERAL_LIST_TH = 'px-3 py-3';
export const GENERAL_LIST_TD = 'px-3 py-3.5';
export const GENERAL_LIST_TABLE = 'w-full table-fixed text-sm';

export function useGeneralListPreferences() {
  const { userSettings } = useUserSettings();
  const { appConfig } = useSettings();

  return useMemo(() => {
    const density = userSettings?.tableDensity;
    const tableDensity: GeneralListTableDensity =
      density === 'compact' || density === 'comfortable' || density === 'normal'
        ? density
        : (appConfig.table.defaultDensity as GeneralListTableDensity);
    const defaultPageSize = userSettings?.defaultPageSize ?? appConfig.table.defaultPageSize;
    return {
      defaultPageSize: typeof defaultPageSize === 'number' && defaultPageSize > 0
        ? defaultPageSize
        : appConfig.table.defaultPageSize,
      pageSizeOptions: appConfig.table.pageSizeOptions,
      tableDensity,
    };
  }, [userSettings?.defaultPageSize, userSettings?.tableDensity, appConfig.table]);
}

export function useGeneralListPageSize() {
  const prefs = useGeneralListPreferences();
  const [itemsPerPage, setItemsPerPage] = useState(prefs.defaultPageSize);

  useEffect(() => {
    setItemsPerPage(prefs.defaultPageSize);
  }, [prefs.defaultPageSize]);

  return { itemsPerPage, setItemsPerPage, ...prefs };
}

export type GeneralListSortConfig = { key: string; direction: 'asc' | 'desc' };
export type GeneralListSortingProps = { sortConfig: GeneralListSortConfig; onSort: (key: string) => void };
const GeneralListSortContext = createContext<GeneralListSortingProps | null>(null);

export function useGeneralListSortContext() {
  return useContext(GeneralListSortContext);
}

export function useGeneralListSort(
  defaultSort: GeneralListSortConfig,
  options?: { defaultDirectionForKey?: (key: string) => 'asc' | 'desc' },
) {
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const handleSort = useCallback((key: string) => {
    setSortConfig((current) => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: options?.defaultDirectionForKey?.(key) ?? 'asc' });
  }, [options]);
  return { sortConfig, handleSort, setSortConfig };
}

export type GeneralListSortableThProps = {
  sortKey: string;
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
};

export function GeneralListSortableTh({
  sortKey, children, className, align = 'left',
}: GeneralListSortableThProps) {
  const { colors } = useTheme();
  const sorting = useGeneralListSortContext();
  const active = sorting?.sortConfig.key === sortKey;
  const direction = sorting?.sortConfig.direction;
  return (
    <th className={cn('sticky top-0 z-10 font-medium', colors.surface, className)}>
      <button
        type="button"
        onClick={() => sorting?.onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wide',
          align === 'right' && 'w-full justify-end',
          align === 'center' && 'w-full justify-center',
          active ? colors.primaryFg : cn(colors.textMuted, 'hover:opacity-90'),
        )}
      >
        {children}
        <span className="text-xs" aria-hidden>{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
}

export type GeneralListSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  title?: string;
  className?: string;
  onSubmit?: () => void;
};

type EmptyState = {
  icon?: React.ReactNode;
  title: string;
  hint?: React.ReactNode;
  action?: React.ReactNode;
};

type Pagination = {
  page: number;
  totalPages: number;
  total: number;
  itemsPerPage: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (size: number) => void;
};

export type GeneralListProps<T extends string = string> = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  tabs?: Array<SegmentedControlOption<T>>;
  activeTab?: T;
  onTabChange?: (value: T) => void;
  tabTrailing?: React.ReactNode;
  search?: GeneralListSearchProps;
  actions?: React.ReactNode;
  filters?: React.ReactNode;
  banner?: React.ReactNode;
  resultSummary?: React.ReactNode;
  hasFilters?: boolean;
  onClearFilters?: () => void;
  isLoading?: boolean;
  loadingLabel?: string;
  isEmpty?: boolean;
  emptyState?: EmptyState;
  children?: React.ReactNode;
  pagination?: Pagination;
  sorting?: GeneralListSortingProps;
  className?: string;
  density?: GeneralListTableDensity;
};

export function GeneralList<T extends string = string>({
  title, description, tabs, activeTab, onTabChange, tabTrailing, search, actions,
  filters, banner, resultSummary,
  hasFilters, onClearFilters, isLoading, loadingLabel = 'Loading…',
  isEmpty, emptyState, children, pagination, sorting, className, density: densityProp,
}: GeneralListProps<T>) {
  const { colors } = useTheme();
  const prefs = useGeneralListPreferences();
  const tableDensity = densityProp ?? prefs.tableDensity;
  const densityClass = tableDensity === 'compact'
    ? '[&_table_th]:!py-2 [&_table_td]:!py-1.5'
    : tableDensity === 'comfortable'
      ? '[&_table_th]:!px-4 [&_table_th]:!py-4 [&_table_td]:!px-4 [&_table_td]:!py-4'
      : '';
  const clearFiltersControl =
    hasFilters && onClearFilters ? (
      <button
        type="button"
        onClick={onClearFilters}
        className={cn('text-xs font-medium hover:underline shrink-0', colors.textSecondary)}
      >
        Clear filters
      </button>
    ) : null;
  const body = (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border)}>
        <div className={cn('relative z-30 flex flex-shrink-0 flex-col gap-2.5 border-b px-4 py-3 sm:px-5', colors.border)}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {(title != null || description != null) && (
                <div className="min-w-0 max-w-xl">
                  {title != null && <h2 className={cn('text-base font-semibold leading-tight', colors.text)}>{title}</h2>}
                  {description != null && (
                    <p className={cn('mt-0.5 text-xs sm:text-sm leading-snug', colors.textMuted)}>{description}</p>
                  )}
                </div>
              )}
              {tabs && activeTab != null && onTabChange && (
                <SegmentedControl options={tabs} value={activeTab} onChange={onTabChange} trailing={tabTrailing} size="sm" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {search && (
                <label className="relative" title={search.title}>
                  <SearchIcon className={cn('pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', colors.textMuted)} />
                  <input
                    value={search.value}
                    onChange={(event) => search.onChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && search.onSubmit) {
                        event.preventDefault();
                        search.onSubmit();
                      }
                    }}
                    placeholder={search.placeholder ?? 'Search…'}
                    title={search.title}
                    className={cn(
                      'w-56 rounded-xl border py-2 pl-9 pr-3 text-sm outline-none sm:w-64',
                      colors.border,
                      colors.surface,
                      colors.text,
                      search.className,
                    )}
                  />
                </label>
              )}
              {actions}
            </div>
          </div>

          {banner != null && banner}

          {(filters != null || resultSummary != null || clearFiltersControl) && (
            <div className="flex flex-wrap items-center gap-2">
              {filters != null && <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{filters}</div>}
              {(resultSummary != null || clearFiltersControl) && (
                <div className={cn('flex flex-wrap items-center gap-2', filters != null ? 'ml-auto' : 'w-full justify-between')}>
                  {resultSummary != null ? resultSummary : <span />}
                  {clearFiltersControl}
                </div>
              )}
            </div>
          )}
        </div>
        {isLoading ? (
          <div className={cn('flex flex-1 flex-col items-center justify-center gap-3 px-6 py-20', colors.textMuted)}>
            <SpinnerIcon className="h-6 w-6" />
            <p className="text-sm">{loadingLabel}</p>
          </div>
        ) : isEmpty && emptyState ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
            {emptyState.icon && <div className={cn('mb-4 h-12 w-12', colors.textMuted)}>{emptyState.icon}</div>}
            <h2 className={cn('text-base font-semibold', colors.text)}>{emptyState.title}</h2>
            {emptyState.hint && <p className={cn('mt-1 max-w-sm text-sm', colors.textMuted)}>{emptyState.hint}</p>}
            {emptyState.action && <div className="mt-5">{emptyState.action}</div>}
          </div>
        ) : (
          <>
            <div className={cn('min-h-0 flex-1 overflow-auto', densityClass)}>{children}</div>
            {pagination && (
              <div className={cn('flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm sm:px-6', colors.border, colors.textSecondary)}>
                <span>Showing {pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.itemsPerPage + 1} to {Math.min(pagination.page * pagination.itemsPerPage, pagination.total)} of {pagination.total} results</span>
                <div className="flex items-center gap-3">
                  <select value={pagination.itemsPerPage} onChange={(event) => pagination.onItemsPerPageChange(Number(event.target.value))} className={cn('rounded-md border px-2 py-1', colors.border, colors.surface, colors.text)}>
                    {pagination.pageSizeOptions.map((size) => <option key={size} value={size}>{size}</option>)}
                  </select>
                  <Button type="button" variant="ghost" size="sm" disabled={pagination.page <= 1} onClick={() => pagination.onPageChange(pagination.page - 1)}>Previous</Button>
                  <span>{pagination.page} / {pagination.totalPages}</span>
                  <Button type="button" variant="ghost" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => pagination.onPageChange(pagination.page + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
  return sorting ? <GeneralListSortContext.Provider value={sorting}>{body}</GeneralListSortContext.Provider> : body;
}

export function GeneralListSearch({ value, onChange, placeholder }: GeneralListSearchProps) {
  return <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder ?? 'Search…'} />;
}
